/**
 * Returns the best available translation that should be shown to the user.
 *
 * Logic:
 * - If the selected language is available, return that
 * - If the fallback language is available, return that
 * - If neither are available, return the first translation
 */
export type Translation<T> = {
  language: string;
  translation: T;
};

const fallbackLanguage = "en";

export function activeTranslationOf<T>(
  // key: language, value: translation
  translations: { [key: string]: T },
  selectedLanguage: string
): Translation<T> | undefined {
  if (translations[selectedLanguage]) {
    return {
      language: selectedLanguage,
      translation: translations[selectedLanguage],
    };
  }
  if (translations[fallbackLanguage]) {
    return {
      language: fallbackLanguage,
      translation: translations[fallbackLanguage],
    };
  }

  // return the first translation if no fallback language is found
  const firstTranslation = Object.entries(translations)[0];
  if (!firstTranslation) {
    return undefined;
  }
  return { language: firstTranslation[0], translation: firstTranslation[1] };
}

/**
 * Creates a copy of translations with "(copy)" appended to each name.
 * Used when duplicating dishes or products.
 */
export function duplicateTranslations<
  T extends { name: string; description: string }
>(translations: { [key: string]: T }): { [key: string]: T } {
  const result: { [key: string]: T } = {};
  for (const langCode in translations) {
    const translation = translations[langCode];
    result[langCode] = {
      ...translation,
      name: `${translation.name} (copy)`,
    };
  }
  return result;
}
